import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Autocomplete, TextField, Typography, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { mutate } from 'swr';
import { useGetEntityNetwork, useCreateEntity } from '../../api/entities';
import { updateAccount } from '../../api/accounts';
import { formatPercentage } from '../../utils/formatters';
import SnackbarContext from '../../context/snackbar-context';
import styles from './AccountSourceEditDialog.module.css';

const ENTITY_KINDS = ['Person', 'Business', 'Trust', 'Estate'];
const ACCOUNTS_CACHE_KEY = 'ACCOUNT_BALANCES_BY_CATEGORY';

const num = (x) => (x === '' || x === null || x === undefined ? null : Number(x));

// Merge a partial account update into the nested balances-by-category cache
// (data[assets|liabilities][category].accounts[]) immutably, so an optimistic source
// edit re-renders the MBS before the save round-trip completes. Exported for testing.
const ACCOUNT_TYPES = ['assets', 'liabilities'];
export function patchAccountInCache(data, id, patch) {
  if (!data) return data;
  const next = { ...data };
  for (const type of ACCOUNT_TYPES) {
    const cats = next[type];
    if (!cats) continue;
    let changed = false;
    const nextCats = {};
    for (const [cat, catData] of Object.entries(cats)) {
      const accounts = catData?.accounts || [];
      const idx = accounts.findIndex((a) => a?.id === id);
      if (idx < 0) { nextCats[cat] = catData; continue; }
      const nextAccounts = accounts.slice();
      const prev = nextAccounts[idx];
      nextAccounts[idx] = {
        ...prev, ...patch,
        latest_balance: { ...(prev.latest_balance || {}), ...(patch.latest_balance || {}) },
      };
      nextCats[cat] = { ...catData, accounts: nextAccounts };
      changed = true;
    }
    if (changed) next[type] = nextCats;
  }
  return next;
}

// Account-ownership Step-3 stated source: the court-claimed source of an account's
// funds — petitioner, respondent, and/or non-party "other" entities (a gift, a
// trust, separate property), each a % of the account with an optional $ override.
// The remainder is the marital presumption (never an entity). On save the party
// rows are mapped back to the petitioner/respondent owning structures (which the
// backend dual-writes to the legacy props, so the change shows on cases still
// reading props), and the rest become other_owning_structure[] (edge-native). A
// missing entity can be created inline. Mirrors AccountTitleEditDialog.
export default function AccountSourceEditDialog({ open, onClose, onSaved, account }) {
  const accountId = account?.id;
  const accountName = account?._displayName || account?.account_holder || 'this account';
  const { data: network, isLoading: entitiesLoading } = useGetEntityNetwork(open);
  const entities = useMemo(() => network?.entities || [], [network]);

  // Party entities — used to route a row to the petitioner/respondent owning
  // structure (vs an "other" source). case_role is assigned_role on the party
  // person (backfilled by the 1.3.0 migration), so it distinguishes the two.
  const partyIds = useMemo(() => {
    const find = (role) => entities.find((e) => e.case_role === role)?.id || null;
    return { petitioner: find('petitioner'), respondent: find('respondent') };
  }, [entities]);

  // Pick-list = network entities + any of this account's current source entities
  // not yet in the network (so a pre-existing source renders instantly).
  const entityOptions = useMemo(() => {
    const byId = new Map(entities.map((e) => [e.id, e]));
    for (const s of (account?.stated_source || [])) {
      if (s?.entity_id && !byId.has(s.entity_id)) {
        byId.set(s.entity_id, { id: s.entity_id, display_name: s.display_name, kind: s.kind });
      }
    }
    return Array.from(byId.values());
  }, [entities, account]);
  const entitiesById = useMemo(() => Object.fromEntries(entityOptions.map((e) => [e.id, e])), [entityOptions]);
  const createEntity = useCreateEntity();
  const { openSnackbarError } = useContext(SnackbarContext) || {};

  const [rows, setRows] = useState([]); // [{ entity_id, pct, value }] — pct whole-number 0-100; value optional $
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newKind, setNewKind] = useState('Business');
  const [newName, setNewName] = useState('');

  // Seed rows from the account's current stated_source[] when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const ss = account?.stated_source || [];
    setRows(ss.length
      ? ss.map((s) => ({ entity_id: s.entity_id, pct: Math.round((Number(s.pct) || 0) * 100), value: s.value_override ?? '' }))
      : [{ entity_id: '', pct: '', value: '' }]);
    setErr(null);
    setCreating(false); setNewName(''); setNewKind('Business');
  }, [open, account]);

  const totalPct = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  const marital = Math.max(0, 100 - totalPct);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { entity_id: '', pct: '', value: '' }]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : [{ entity_id: '', pct: '', value: '' }]));

  const validationError = () => {
    if (totalPct > 100.0001) return 'Source can’t exceed 100% — the remainder is marital.';
    const ids = rows.filter((r) => r.entity_id).map((r) => r.entity_id);
    if (new Set(ids).size !== ids.length) return 'An entity is listed more than once.';
    for (const r of rows) {
      if (r.entity_id && !(Number(r.pct) > 0)) return 'Each source entity needs a percent greater than 0.';
    }
    return null;
  };

  // Create a new entity inline and drop it into the first empty row (else append).
  const handleCreateEntity = async () => {
    const name = newName.trim();
    if (!name) { setErr('Enter a name for the new entity.'); return; }
    setBusy(true); setErr(null);
    try {
      const created = await createEntity({ kind: newKind, display_name: name, name });
      const id = created?.id;
      if (id) {
        setRows((rs) => {
          const emptyIdx = rs.findIndex((r) => !r.entity_id);
          if (emptyIdx >= 0) return rs.map((r, i) => (i === emptyIdx ? { ...r, entity_id: id } : r));
          return [...rs, { entity_id: id, pct: '', value: '' }];
        });
      }
      setCreating(false); setNewName('');
    } catch (e) {
      setErr((e && (e.message || e.toString())) || 'Couldn’t create the entity.');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const v = validationError();
    if (v) { setErr(v); return; }
    const valid = rows.filter((r) => r.entity_id && Number(r.pct) > 0);
    // Route party rows to the owning structures (the backend dual-writes the legacy
    // props from these); everything else is an "other" source. The backend caps
    // Σ ≤ 1 and preserves the marital remainder.
    const toStructure = (r) => ({ percentage: Number(r.pct) / 100, value: num(r.value), driver: 'percentage' });
    let petitioner_owning_structure = { percentage: 0, value: null, driver: 'percentage' };
    let respondent_owning_structure = { percentage: 0, value: null, driver: 'percentage' };
    const other_owning_structure = [];
    for (const r of valid) {
      if (r.entity_id === partyIds.petitioner) petitioner_owning_structure = toStructure(r);
      else if (r.entity_id === partyIds.respondent) respondent_owning_structure = toStructure(r);
      else other_owning_structure.push({ entity_id: r.entity_id, percentage: Number(r.pct) / 100, value: num(r.value) });
    }
    // Optimistically patch the edited account into the balances cache so the MBS (which
    // reads the node props _*_owning_pct / _stated_*_pct) reflects the new allocation
    // immediately, then persist in the background and reconcile with the server-derived
    // values. A failure rolls back (revalidate) + toasts. Close first so the optimistic
    // re-render is what the user lands on.
    const r6 = (x) => Math.round(x * 1e6) / 1e6;
    const end = account?.latest_balance?.end_balance || 0;
    const petPct = petitioner_owning_structure.percentage;
    const resPct = respondent_owning_structure.percentage;
    const otherPct = r6(other_owning_structure.reduce((s, o) => s + (o.percentage || 0), 0));
    const optimistic = {
      _petitioner_owning_pct: petPct,
      _respondent_owning_pct: resPct,
      _stated_other_pct: otherPct,
      _stated_marital_pct: r6(Math.max(0, 1 - petPct - resPct - otherPct)),
      petitioner_owning_structure,
      respondent_owning_structure,
      stated_source: [
        ...(petPct > 0 ? [{ entity_id: partyIds.petitioner, pct: petPct, value_override: petitioner_owning_structure.value }] : []),
        ...(resPct > 0 ? [{ entity_id: partyIds.respondent, pct: resPct, value_override: respondent_owning_structure.value }] : []),
        ...other_owning_structure.map((o) => ({ entity_id: o.entity_id, pct: o.percentage, value_override: o.value })),
      ],
      latest_balance: {
        _petitioner_owning_value: petitioner_owning_structure.value ?? petPct * end,
        _respondent_owning_value: respondent_owning_structure.value ?? resPct * end,
      },
    };

    onClose();
    mutate(ACCOUNTS_CACHE_KEY, (cur) => patchAccountInCache(cur, accountId, optimistic), { revalidate: false });
    try {
      await updateAccount(accountId, { petitioner_owning_structure, respondent_owning_structure, other_owning_structure });
      await mutate(ACCOUNTS_CACHE_KEY); // reconcile with the server-derived values
      onSaved?.();
    } catch (e) {
      await mutate(ACCOUNTS_CACHE_KEY); // roll back to server state
      openSnackbarError?.('Couldn’t save the source of funds.');
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Source of funds — {accountName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" className={styles.intro}>
          Where did this account’s funds come from? Assign a share to the petitioner, the
          respondent, and/or any other source (a gift, separate property, a trust). Any remainder
          is presumed marital. The $ override is optional — leave it blank to use the percent.
        </Typography>

        {rows.map((r, i) => (
          <div key={i} className={styles.row}>
            <Autocomplete
              className={styles.entity}
              options={entityOptions}
              loading={entitiesLoading}
              getOptionLabel={(o) => (typeof o === 'string' ? o : (o.display_name || ''))}
              isOptionEqualToValue={(o, val) => o.id === (val?.id || val)}
              value={entitiesById[r.entity_id] || null}
              onChange={(e, val) => setRow(i, { entity_id: val?.id || '' })}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id}>
                  {o.display_name}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {o.id === partyIds.petitioner ? 'Petitioner' : o.id === partyIds.respondent ? 'Respondent' : o.kind}
                  </Typography>
                </MenuItem>
              )}
              renderInput={(params) => <TextField {...params} label="Source" placeholder="Select or search…" size="small" />}
            />
            <TextField
              className={styles.pct}
              label="%" type="number" size="small"
              value={r.pct}
              onChange={(e) => setRow(i, { pct: e.target.value })}
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              className={styles.value}
              label="$ (optional)" type="number" size="small"
              value={r.value}
              onChange={(e) => setRow(i, { value: e.target.value })}
            />
            <IconButton aria-label="Remove source" size="small" className={styles.removeBtn} onClick={() => removeRow(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}

        <div className={styles.addActions}>
          <Button startIcon={<AddIcon />} size="small" onClick={addRow}>Add source</Button>
          {!creating && <Button size="small" onClick={() => { setCreating(true); setErr(null); }}>Create new entity…</Button>}
        </div>

        {creating && (
          <div className={styles.createBox}>
            <FormControl size="small" className={styles.createKind}>
              <InputLabel id="new-source-entity-kind">Kind</InputLabel>
              <Select labelId="new-source-entity-kind" label="Kind" value={newKind} onChange={(e) => setNewKind(e.target.value)}>
                {ENTITY_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              className={styles.createName}
              label="Name" size="small"
              value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Legal / display name"
            />
            <Button size="small" variant="outlined" onClick={handleCreateEntity} disabled={busy}>Create</Button>
            <Button size="small" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
          </div>
        )}

        <div className={styles.footer}>
          <Typography variant="body2">Sourced: {formatPercentage(totalPct / 100)}</Typography>
          <Typography variant="body2" color="text.secondary">Marital (remainder): {formatPercentage(marital / 100)}</Typography>
        </div>
        {err && <Typography variant="body2" color="error" className={styles.error}>{err}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={busy}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
